import apiClient from '../config/api';
import type { ATSData, AgreementDetail, CalculationResult } from '../types';

export interface ATSResponse {
  atsData: ATSData;
  hospitalId: string;
  hospitalUuid: string;
  hospitalName: string;
}

export async function fetchATSData(apptId: string): Promise<ATSResponse> {
  const response = await apiClient.get(`/api/ats/appointment/${encodeURIComponent(apptId)}`);
  const d = response.data;
  return {
    atsData: mapATSData(d.atsData),
    hospitalId: d.hospitalId,
    hospitalUuid: d.hospitalUuid,
    hospitalName: d.hospitalName,
  };
}

function mapATSData(raw: Record<string, unknown>): ATSData {
  return {
    apptId: String(raw.apptid ?? raw.appt_id ?? ''),
    patientId: String(raw.patient_id ?? ''),
    groupName: String(raw.group_name ?? ''),
    appType: String(raw.app_type ?? ''),
    hospitalId: String(raw.hospital_id ?? ''),
    hospitalName: String(raw.hospital_name ?? ''),
    city: String(raw.city ?? ''),
    patientName: String(raw.patient_name ?? ''),
    doa: String(raw.doa ?? ''),
    otDate: String(raw.ot_date ?? ''),
    ipdStatus: String(raw.ipd_status ?? ''),
    surgeryCount: Number(raw.surgery_count ?? 0),
    dod: String(raw.dod ?? ''),
    subCategory: String(raw.sub_category ?? ''),
    category: String(raw.category ?? ''),
    doctorName: String(raw.doctor_name ?? ''),
    doctorCode: String(raw.doctor_code ?? ''),
    paymentMode: String(raw.payment_mode ?? ''),
    opthalNonOpthal: String(raw.opthal_non_opthal ?? ''),
    tpaName: String(raw.tpa_name ?? ''),
    insuranceCompany: String(raw.insurance_company ?? ''),
    disease: String(raw.disease ?? ''),
    techAmount: Number(raw.tech_amount ?? 0),
    grossBillAmount: Number(raw.gross_bill_amount ?? 0),
    discount: Number(raw.discount ?? 0),
    netBillAmount: Number(raw.net_bill_amount ?? 0),
    approvalAmount: Number(raw.approval_amount ?? 0),
    extraCashReceived: Number(raw.extra_cash_received ?? 0),
    projectedAts: Number(raw.projected_ats ?? 0),
    atsWithGst: Number(raw.ats_with_gst ?? 0),
    atsWithoutGst: Number(raw.ats_without_gst ?? 0),
    atsStatus: String(raw.ats_status ?? ''),
    atsCollectionAuditStatus: String(raw.ats_collection_audit_status ?? ''),
    atsForDrPayout: Number(raw.ats_for_dr_payout ?? 0),
    revenueLossCalculated: Number(raw.revenue_loss_calculated ?? 0),
    revenueLossCopsDashboard: Number(raw.revenue_loss_cops_dashboard ?? 0),
    finalAtsForSharingOtherPayout: Number(raw.final_ats_for_sharing_other_payout ?? 0),
    collectableFromPatient: Number(raw.collectable_from_patient ?? 0),
    totalCollection: Number(raw.total_collection ?? 0),
    collectedAtGhv: Number(raw.collected_at_ghv ?? 0),
    collectedAtHospital: Number(raw.collected_at_hospital ?? 0),
    insuranceSettlementAmount: Number(raw.insurance_settlement_amount ?? 0),
    excessAmount: Number(raw.excess_amount ?? 0),
    settlementReceivedBy: String(raw.settlement_received_by ?? ''),
    totalAmountReceivedByGhv: Number(raw.total_amount_received_by_ghv ?? 0),
    totalAmountReceivedByHospital: Number(raw.total_amount_received_by_hospital ?? 0),
    multipleSurgeryRemarks: String(raw.multiple_surgery_remarks ?? ''),
    stayDays: Number(raw.stay_days ?? 0),
    lensName: String(raw.lens_name ?? ''),
    vendorPriceIncludingTax: Number(raw.vendor_price_including_tax ?? 0),
    lensCostMrp: Number(raw.lens_cost_mrp ?? 0),
    ghvImplant: Number(raw.ghv_implant ?? 0),
    ghvConsumable: Number(raw.ghv_consumable ?? 0),
    ghvEquipment: Number(raw.ghv_equipment ?? 0),
    hosImplantCount: Number(raw.hos_implant_count ?? 0),
    hosEquipmentCount: Number(raw.hos_equipment_count ?? 0),
    hosConsumableCount: Number(raw.hos_consumable_count ?? 0),
    pcImplantCount: Number(raw.pc_implant_count ?? 0),
    pcEquipmentCount: Number(raw.pc_equipment_count ?? 0),
    pcConsumableCount: Number(raw.pc_consumable_count ?? 0),
    lensPendency: String(raw.lens_pendency ?? ''),
    implantCostPendency: String(raw.implant_cost_pendency ?? ''),
    equipmentCostPendency: String(raw.equipment_cost_pendency ?? ''),
    consumableCostPendency: String(raw.consumable_cost_pendency ?? ''),
    concernedTeam: String(raw.concerned_team ?? ''),
    assistSubCategory: String(raw.assist_sub_category ?? ''),
    bdName: String(raw.bd_name ?? ''),
    otCount: Number(raw.ot_count ?? 0),
    currentStay: Number(raw.current_stay ?? 0),
    band: String(raw.band ?? ''),
    minorDisease: String(raw.minor_disease ?? ''),
    aging: Number(raw.aging ?? 0),
    band2: String(raw.band_2 ?? ''),
    bdName2: String(raw.bd_name_2 ?? ''),
    leadId: String(raw.lead_id ?? ''),
    inhouseTag: String(raw.inhouse_tag ?? ''),
    createdBy: String(raw.created_by ?? ''),
    leadSource: String(raw.lead_source ?? ''),
    partnerHospitalName: String(raw.partner_hospital_name ?? ''),
    noOfIpds: Number(raw.no_of_ipds ?? 0),
    ...raw,
  };
}

export async function executeCalculation(
  apptId: string,
  agreementId: number,
  inputValues: Record<string, unknown>
): Promise<CalculationResult> {
  const response = await apiClient.post('/api/calculations/calculate', {
    apptId,
    agreementId,
    inputValues,
  });
  return response.data;
}

export interface ValidateCalculationParams {
  apptId: string;
  hospitalUuid: string;
  agreementId: number;
  agreementVersion: number;
  atsData: ATSData;
  inputValues: Record<string, unknown>;
  calculatedParameters: Record<string, unknown>;
  hospitalShare: number;
  arAp: number;
  validationStatus: 'OK' | 'NOT_OK';
  errorCategory?: string;
  remarks?: string;
  executionTimeMs: number;
}

export async function validateCalculation(
  params: ValidateCalculationParams
): Promise<{ success: boolean; calculationId: number }> {
  const response = await apiClient.post('/api/calculations/validate', params);
  return response.data;
}

export async function getActiveAgreementForHospital(
  hospitalUuid: string,
  appointmentDate?: string
): Promise<AgreementDetail | null> {
  const params: Record<string, string> = { hospitalUuid };
  if (appointmentDate) {
    params.date = appointmentDate;
  }
  const response = await apiClient.get('/api/agreements/active', {
    params,
  });
  if (!response.data.agreement) return null;
  return mapAgreementDetail(response.data.agreement);
}

function mapAgreementDetail(data: Record<string, unknown>): AgreementDetail {
  return {
    agreementId: data.agreement_id as number,
    hospitalUuid: data.hospital_uuid as string,
    hospitalName: data.hospital_name as string | undefined,
    validFrom: data.valid_from as string,
    validTill: data.valid_till as string,
    hospitalShareNlFormula: data.hospital_share_nl_formula as string,
    hospitalShareExecutable: data.hospital_share_executable as string,
    arApNlFormula: data.ar_ap_nl_formula as string,
    arApExecutable: data.ar_ap_executable as string,
    version: data.version as number,
    createdBy: data.created_by as string,
    modifiedBy: data.modified_by as string,
    createdAt: data.created_at as string,
    modifiedAt: data.modified_at as string,
    inputParameters: Array.isArray(data.inputParameters)
      ? data.inputParameters.map((p: Record<string, unknown>) => ({
          parameterId: p.parameter_id as number,
          parameterName: p.parameter_name as string,
          parameterType: p.parameter_type as 'INPUT' | 'CALCULATED',
          sourceType: p.source_type as 'SYSTEM' | 'MANUAL' | undefined,
          dataType: p.data_type as 'TEXT' | 'NUMBER' | 'DATE' | 'DROPDOWN' | undefined,
          validationRules: p.validation_rules as any,
          dropdownValues: p.dropdown_values as string[] | undefined,
          displayOrder: p.display_order as number,
          isActive: p.is_active as boolean,
          createdBy: p.created_by as string,
          modifiedBy: p.modified_by as string,
          createdAt: p.created_at as string,
          modifiedAt: p.modified_at as string,
        }))
      : undefined,
    calculatedParameters: Array.isArray(data.calculatedParameters)
      ? data.calculatedParameters.map((p: Record<string, unknown>) => ({
          calculatedParameterId: p.calculated_parameter_id as number,
          agreementId: p.agreement_id as number,
          parameterName: p.parameter_name as string,
          naturalLanguageFormula: p.natural_language_formula as string,
          executableFormula: p.executable_formula as string,
          executionOrder: p.execution_order as number,
          createdAt: p.created_at as string,
        }))
      : undefined,
  };
}

// --- Recalculation ---

export interface CalculationDifference {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  percentChange?: number;
  absoluteChange?: number;
}

export interface RecalculationResponse {
  success: boolean;
  oldCalculation: Record<string, unknown>;
  newCalculation: Record<string, unknown>;
  differences: CalculationDifference[];
  errors?: string[];
}

export async function recalculateSingle(
  calculationId: number,
  useCurrentAgreement: boolean
): Promise<RecalculationResponse> {
  const response = await apiClient.post('/api/calculations/recalculate', {
    calculationId,
    useCurrentAgreement,
  });
  return response.data;
}

export async function getCalculationHistory(
  apptId: string
): Promise<{ calculations: Record<string, unknown>[]; total: number }> {
  const response = await apiClient.get(
    `/api/calculations/history/${encodeURIComponent(apptId)}`
  );
  return response.data;
}

export async function getCalculationDetail(
  calculationId: number
): Promise<{ calculation: Record<string, unknown> }> {
  const response = await apiClient.get(
    `/api/calculations/${encodeURIComponent(calculationId)}`
  );
  return response.data;
}

// --- Bulk Recalculation ---

export interface BulkRecalculationJobStatus {
  jobId: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  progress: {
    processed: number;
    total: number;
    changed: number;
    errors: number;
  };
  result?: unknown;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

export async function startBulkRecalculation(
  hospitalUuid: string,
  dateFrom: string,
  dateTill: string
): Promise<{ jobId: string }> {
  const response = await apiClient.post('/api/calculations/bulk-recalculate', {
    hospitalUuid,
    dateFrom,
    dateTill,
  });
  return response.data;
}

export async function getBulkRecalculationStatus(
  jobId: string
): Promise<BulkRecalculationJobStatus> {
  const response = await apiClient.get(
    `/api/calculations/bulk-recalculate/${encodeURIComponent(jobId)}/status`
  );
  return response.data;
}
